/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {looseIdentical, stringify} from '../../util';
import {isListLikeIterable, iterateListLike} from '../change_detection_util';
import {ChangeDetectorRef} from '../change_detector_ref';

import {IterableChangeRecord, IterableChanges, IterableDiffer, IterableDifferFactory, NgIterable, TrackByFunction} from './iterable_differs';
export class DefaultIterableDifferFactory implements IterableDifferFactory {
constructor() {}
/**
 * @param {?} obj
 * @return {?}
 */
supports(obj: Object|null|undefined): boolean { return isListLikeIterable(obj); }

  create<V>(trackByFn?: TrackByFunction<V>): DefaultIterableDiffer<V>;
/**
 * @deprecated v4.0.0 - ChangeDetectorRef is not used and is no longer a parameter
 * @template V
 * @param {?=} cdRefOrTrackBy
 * @param {?=} trackByFn
 * @return {?}
 */
create<V>(cdRefOrTrackBy?: ChangeDetectorRef|TrackByFunction<V>, trackByFn?: TrackByFunction<V>):
      DefaultIterableDiffer<V> {
    return new DefaultIterableDiffer<V>(trackByFn || /** @type {?} */(( <TrackByFunction<any>>cdRefOrTrackBy)));
  }
}

const /** @type {?} */ trackByIdentity = (index: number, item: any) => item;
/**
 * @deprecated v4.0.0 - Should not be part of public API.
 */
export class DefaultIterableDiffer<V> implements IterableDiffer<V>, IterableChanges<V> {
private _length: number = 0;
private _collection: NgIterable<V>|null = null;
private _linkedRecords: _DuplicateMap<V>|null = null;
private _unlinkedRecords: _DuplicateMap<V>|null = null;
private _previousItHead: IterableChangeRecord_<V>|null = null;
private _itHead: IterableChangeRecord_<V>|null = null;
private _itTail: IterableChangeRecord_<V>|null = null;
private _additionsHead: IterableChangeRecord_<V>|null = null;
private _additionsTail: IterableChangeRecord_<V>|null = null;
private _movesHead: IterableChangeRecord_<V>|null = null;
private _movesTail: IterableChangeRecord_<V>|null = null;
private _removalsHead: IterableChangeRecord_<V>|null = null;
private _removalsTail: IterableChangeRecord_<V>|null = null;
private _identityChangesHead: IterableChangeRecord_<V>|null = null;
private _identityChangesTail: IterableChangeRecord_<V>|null = null;
private _trackByFn: TrackByFunction<V>;
/**
 * @param {?=} trackByFn
 */
constructor(trackByFn?: TrackByFunction<V>) { this._trackByFn = trackByFn || trackByIdentity; }
/**
 * @return {?}
 */
get collection() { return this._collection; }
/**
 * @return {?}
 */
get length(): number { return this._length; }
/**
 * @param {?} fn
 * @return {?}
 */
forEachItem(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._itHead; record !== null; record = record._next) {
      fn(record);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachOperation(
      fn: (item: IterableChangeRecord<V>, previousIndex: number|null, currentIndex: number|null) =>
          void) {
    let /** @type {?} */ nextIt = this._itHead;
    let /** @type {?} */ nextRemove = this._removalsHead;
    let /** @type {?} */ addRemoveOffset = 0;
    let /** @type {?} */ moveOffsets: number[]|null = null;
    while (nextIt || nextRemove) {
      // Figure out which is the next record to process
      // Order: remove, add, move
      const /** @type {?} */ record: IterableChangeRecord<V> = !nextRemove ||
              nextIt && /** @type {?} */((
                  nextIt.currentIndex)) <
                      getPreviousIndex(nextRemove, addRemoveOffset, moveOffsets) ? /** @type {?} */((
          nextIt)) :
          nextRemove;
      const /** @type {?} */ adjPreviousIndex = getPreviousIndex(record, addRemoveOffset, moveOffsets);
      const /** @type {?} */ currentIndex = record.currentIndex;

      // consume the item, and adjust the addRemoveOffset and update moveDistance if necessary
      if (record === nextRemove) {
        addRemoveOffset--;
        nextRemove = nextRemove._nextRemoved;
      } else {
        nextIt = /** @type {?} */(( nextIt))._next;
        if (record.previousIndex == null) {
          addRemoveOffset++;
        } else {
          // INVARIANT:  currentIndex < previousIndex
          if (!moveOffsets) moveOffsets = [];
          const /** @type {?} */ localMovePreviousIndex = adjPreviousIndex - addRemoveOffset;
          const /** @type {?} */ localCurrentIndex = /** @type {?} */(( currentIndex)) - addRemoveOffset;
          if (localMovePreviousIndex != localCurrentIndex) {
            for (let /** @type {?} */ i = 0; i < localMovePreviousIndex; i++) {
              const /** @type {?} */ offset = i < moveOffsets.length ? moveOffsets[i] : (moveOffsets[i] = 0);
              const /** @type {?} */ index = offset + i;
              if (localCurrentIndex <= index && index < localMovePreviousIndex) {
                moveOffsets[i] = offset + 1;
              }
            }
            const /** @type {?} */ previousIndex = record.previousIndex;
            moveOffsets[previousIndex] = localCurrentIndex - localMovePreviousIndex;
          }
        }
      }

      if (adjPreviousIndex !== currentIndex) {
        fn(record, adjPreviousIndex, currentIndex);
      }
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachPreviousItem(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._previousItHead; record !== null; record = record._nextPrevious) {
      fn(record);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachAddedItem(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._additionsHead; record !== null; record = record._nextAdded) {
      fn(record);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachMovedItem(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._movesHead; record !== null; record = record._nextMoved) {
      fn(record);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachRemovedItem(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._removalsHead; record !== null; record = record._nextRemoved) {
      fn(record);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
forEachIdentityChange(fn: (record: IterableChangeRecord_<V>) => void) {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._identityChangesHead; record !== null; record = record._nextIdentityChange) {
      fn(record);
    }
  }
/**
 * @param {?} collection
 * @return {?}
 */
diff(collection: NgIterable<V>): DefaultIterableDiffer<V>|null {
    if (collection == null) collection = [];
    if (!isListLikeIterable(collection)) {
      throw new Error(
          `Error trying to diff '${stringify(collection)}'. Only arrays and iterables are allowed`);
    }

    if (this.check(collection)) {
      return this;
    } else {
      return null;
    }
  }
/**
 * @return {?}
 */
onDestroy() {}
/**
 * @param {?} collection
 * @return {?}
 */
check(collection: NgIterable<V>): boolean {
    this._reset();

    let /** @type {?} */ record: IterableChangeRecord_<V>|null = this._itHead;
    let /** @type {?} */ mayBeDirty: boolean = false;
    let /** @type {?} */ index: number;
    let /** @type {?} */ item: V;
    let /** @type {?} */ itemTrackBy: any;
    if (Array.isArray(collection)) {
      this._length = collection.length;

      for (let /** @type {?} */ index = 0; index < this._length; index++) {
        item = collection[index];
        itemTrackBy = this._trackByFn(index, item);
        if (record === null || !looseIdentical(record.trackById, itemTrackBy)) {
          record = this._mismatch(record, item, itemTrackBy, index);
          mayBeDirty = true;
        } else {
          if (mayBeDirty) {
            // TODO(misko): can we limit this to duplicates only?
            record = this._verifyReinsertion(record, item, itemTrackBy, index);
          }
          if (!looseIdentical(record.item, item)) this._addIdentityChange(record, item);
        }

        record = record._next;
      }
    } else {
      index = 0;
      iterateListLike(collection, (item: V) => {
        itemTrackBy = this._trackByFn(index, item);
        if (record === null || !looseIdentical(record.trackById, itemTrackBy)) {
          record = this._mismatch(record, item, itemTrackBy, index);
          mayBeDirty = true;
        } else {
          if (mayBeDirty) {
            // TODO(misko): can we limit this to duplicates only?
            record = this._verifyReinsertion(record, item, itemTrackBy, index);
          }
          if (!looseIdentical(record.item, item)) this._addIdentityChange(record, item);
        }
        record = record._next;
        index++;
      });
      this._length = index;
    }

    this._truncate(record);
    this._collection = collection;
    return this.isDirty;
  }
/**
 * @return {?}
 */
get isDirty(): boolean {
    return this._additionsHead !== null || this._movesHead !== null ||
        this._removalsHead !== null || this._identityChangesHead !== null;
  }
/**
 * Reset the state of the change objects to show no changes. This means set previousKey to
 * currentKey, and clear all of the queues (additions, moves, removals).
 * Set the previousIndexes of moved and added items to their currentIndexes
 * Reset the list of additions, moves and removals
 * 
 * \@internal
 * @return {?}
 */
_reset() {
    if (this.isDirty) {
      let /** @type {?} */ record: IterableChangeRecord_<V>|null;
      let /** @type {?} */ nextRecord: IterableChangeRecord_<V>|null;

      for (record = this._previousItHead = this._itHead; record !== null; record = record._next) {
        record._nextPrevious = record._next;
      }

      for (record = this._additionsHead; record !== null; record = record._nextAdded) {
        record.previousIndex = record.currentIndex;
      }
      this._additionsHead = this._additionsTail = null;

      for (record = this._movesHead; record !== null; record = nextRecord) {
        record.previousIndex = record.currentIndex;
        nextRecord = record._nextMoved;
      }
      this._movesHead = this._movesTail = null;
      this._removalsHead = this._removalsTail = null;
      this._identityChangesHead = this._identityChangesTail = null;

      // todo(vicb) when assert gets supported
      // assert(!this.isDirty);
    }
  }
/**
 * This is the core function which handles differences between collections.
 * 
 * - `record` is the record which we saw at this position last time. If null then it is a new
 *   item.
 * - `item` is the current item in the collection
 * - `index` is the position of the item in the collection
 * 
 * \@internal
 * @param {?} record
 * @param {?} item
 * @param {?} itemTrackBy
 * @param {?} index
 * @return {?}
 */
_mismatch(record: IterableChangeRecord_<V>|null, item: V, itemTrackBy: any, index: number):
      IterableChangeRecord_<V> {
    // The previous record after which we will append the current one.
    let /** @type {?} */ previousRecord: IterableChangeRecord_<V>|null;

    if (record === null) {
      previousRecord = this._itTail;
    } else {
      previousRecord = record._prev;
      // Remove the record from the collection since we know it does not match the item.
      this._remove(record);
    }

    // Attempt to see if we have seen the item before.
    record = this._linkedRecords === null ? null : this._linkedRecords.get(itemTrackBy, index);
    if (record !== null) {
      // We have seen this before, we need to move it forward in the collection.
      // But first we need to check if identity changed, so we can update in view if necessary
      if (!looseIdentical(record.item, item)) this._addIdentityChange(record, item);

      this._moveAfter(record, previousRecord, index);
    } else {
      // Never seen it, check evicted list.
      record = this._unlinkedRecords === null ? null : this._unlinkedRecords.get(itemTrackBy, null);
      if (record !== null) {
        // It is an item which we have evicted earlier: reinsert it back into the list.
        // But first we need to check if identity changed, so we can update in view if necessary
        if (!looseIdentical(record.item, item)) this._addIdentityChange(record, item);

        this._reinsertAfter(record, previousRecord, index);
      } else {
        // It is a new item: add it.
        record =
            this._addAfter(new IterableChangeRecord_<V>(item, itemTrackBy), previousRecord, index);
      }
    }
    return record;
  }
/**
 * This check is only needed if an array contains duplicates. (Short circuit of nothing dirty)
 * 
 * Use case: `[a, a]` => `[b, a, a]`
 * 
 * If we did not have this check then the insertion of `b` would:
 *   1) evict first `a`
 *   2) insert `b` at `0` index.
 *   3) leave `a` at index `1` as is. <-- this is wrong!
 *   3) reinsert `a` at index 2. <-- this is wrong!
 * 
 * The correct behavior is:
 *   1) evict first `a`
 *   2) insert `b` at `0` index.
 *   3) reinsert `a` at index 1.
 *   3) move `a` at from `1` to `2`.
 * 
 * 
 * Double check that we have not evicted a duplicate item. We need to check if the item type may
 * have already been removed:
 * The insertion of b will evict the first 'a'. If we don't reinsert it now it will be reinserted
 * at the end. Which will show up as the two 'a's switching position. This is incorrect, since a
 * better way to think of it is as insert of 'b' rather then switch 'a' with 'b' and then add 'a'
 * at the end.
 * 
 * \@internal
 * @param {?} record
 * @param {?} item
 * @param {?} itemTrackBy
 * @param {?} index
 * @return {?}
 */
_verifyReinsertion(record: IterableChangeRecord_<V>, item: V, itemTrackBy: any, index: number):
      IterableChangeRecord_<V> {
    let /** @type {?} */ reinsertRecord: IterableChangeRecord_<V>|null =
        this._unlinkedRecords === null ? null : this._unlinkedRecords.get(itemTrackBy, null);
    if (reinsertRecord !== null) {
      record = this._reinsertAfter(reinsertRecord, /** @type {?} */(( record._prev)), index);
    } else if (record.currentIndex != index) {
      record.currentIndex = index;
      this._addToMoves(record, index);
    }
    return record;
  }
/**
 * Get rid of any excess {\@link IterableChangeRecord_}s from the previous collection
 * 
 * - `record` The first excess {\@link IterableChangeRecord_}.
 * 
 * \@internal
 * @param {?} record
 * @return {?}
 */
_truncate(record: IterableChangeRecord_<V>|null) {
    // Anything after that needs to be removed;
    while (record !== null) {
      const /** @type {?} */ nextRecord: IterableChangeRecord_<V>|null = record._next;
      this._addToRemovals(this._unlink(record));
      record = nextRecord;
    }
    if (this._unlinkedRecords !== null) {
      this._unlinkedRecords.clear();
    }

    if (this._additionsTail !== null) {
      this._additionsTail._nextAdded = null;
    }
    if (this._movesTail !== null) {
      this._movesTail._nextMoved = null;
    }
    if (this._itTail !== null) {
      this._itTail._next = null;
    }
    if (this._removalsTail !== null) {
      this._removalsTail._nextRemoved = null;
    }
    if (this._identityChangesTail !== null) {
      this._identityChangesTail._nextIdentityChange = null;
    }
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} prevRecord
 * @param {?} index
 * @return {?}
 */
_reinsertAfter(
      record: IterableChangeRecord_<V>, prevRecord: IterableChangeRecord_<V>|null,
      index: number): IterableChangeRecord_<V> {
    if (this._unlinkedRecords !== null) {
      this._unlinkedRecords.remove(record);
    }
    const /** @type {?} */ prev = record._prevRemoved;
    const /** @type {?} */ next = record._nextRemoved;

    if (prev === null) {
      this._removalsHead = next;
    } else {
      prev._nextRemoved = next;
    }
    if (next === null) {
      this._removalsTail = prev;
    } else {
      next._prevRemoved = prev;
    }

    this._insertAfter(record, prevRecord, index);
    this._addToMoves(record, index);
    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} prevRecord
 * @param {?} index
 * @return {?}
 */
_moveAfter(
      record: IterableChangeRecord_<V>, prevRecord: IterableChangeRecord_<V>|null,
      index: number): IterableChangeRecord_<V> {
    this._unlink(record);
    this._insertAfter(record, prevRecord, index);
    this._addToMoves(record, index);
    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} prevRecord
 * @param {?} index
 * @return {?}
 */
_addAfter(
      record: IterableChangeRecord_<V>, prevRecord: IterableChangeRecord_<V>|null,
      index: number): IterableChangeRecord_<V> {
    this._insertAfter(record, prevRecord, index);

    if (this._additionsTail === null) {
      // todo(vicb)
      // assert(this._additionsHead === null);
      this._additionsTail = this._additionsHead = record;
    } else {
      // todo(vicb)
      // assert(_additionsTail._nextAdded === null);
      // assert(record._nextAdded === null);
      this._additionsTail = this._additionsTail._nextAdded = record;
    }
    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} prevRecord
 * @param {?} index
 * @return {?}
 */
_insertAfter(
      record: IterableChangeRecord_<V>, prevRecord: IterableChangeRecord_<V>|null,
      index: number): IterableChangeRecord_<V> {
    // todo(vicb)
    // assert(record != prevRecord);
    // assert(record._next === null);
    // assert(record._prev === null);

    const /** @type {?} */ next: IterableChangeRecord_<V>|null =
        prevRecord === null ? this._itHead : prevRecord._next;
    // todo(vicb)
    // assert(next != record);
    // assert(prevRecord != record);
    record._next = next;
    record._prev = prevRecord;
    if (next === null) {
      this._itTail = record;
    } else {
      next._prev = record;
    }
    if (prevRecord === null) {
      this._itHead = record;
    } else {
      prevRecord._next = record;
    }

    if (this._linkedRecords === null) {
      this._linkedRecords = new _DuplicateMap<V>();
    }
    this._linkedRecords.put(record);

    record.currentIndex = index;
    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @return {?}
 */
_remove(record: IterableChangeRecord_<V>): IterableChangeRecord_<V> {
    return this._addToRemovals(this._unlink(record));
  }
/**
 * \@internal
 * @param {?} record
 * @return {?}
 */
_unlink(record: IterableChangeRecord_<V>): IterableChangeRecord_<V> {
    if (this._linkedRecords !== null) {
      this._linkedRecords.remove(record);
    }

    const /** @type {?} */ prev = record._prev;
    const /** @type {?} */ next = record._next;

    // todo(vicb)
    // assert((record._prev = null) === null);
    // assert((record._next = null) === null);

    if (prev === null) {
      this._itHead = next;
    } else {
      prev._next = next;
    }
    if (next === null) {
      this._itTail = prev;
    } else {
      next._prev = prev;
    }

    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} toIndex
 * @return {?}
 */
_addToMoves(record: IterableChangeRecord_<V>, toIndex: number): IterableChangeRecord_<V> {
    // todo(vicb)
    // assert(record._nextMoved === null);

    if (record.previousIndex === toIndex) {
      return record;
    }

    if (this._movesTail === null) {
      // todo(vicb)
      // assert(_movesHead === null);
      this._movesTail = this._movesHead = record;
    } else {
      // todo(vicb)
      // assert(_movesTail._nextMoved === null);
      this._movesTail = this._movesTail._nextMoved = record;
    }

    return record;
  }
/**
 * @param {?} record
 * @return {?}
 */
private _addToRemovals(record: IterableChangeRecord_<V>): IterableChangeRecord_<V> {
    if (this._unlinkedRecords === null) {
      this._unlinkedRecords = new _DuplicateMap<V>();
    }
    this._unlinkedRecords.put(record);
    record.currentIndex = null;
    record._nextRemoved = null;

    if (this._removalsTail === null) {
      // todo(vicb)
      // assert(_removalsHead === null);
      this._removalsTail = this._removalsHead = record;
      record._prevRemoved = null;
    } else {
      // todo(vicb)
      // assert(_removalsTail._nextRemoved === null);
      // assert(record._nextRemoved === null);
      record._prevRemoved = this._removalsTail;
      this._removalsTail = this._removalsTail._nextRemoved = record;
    }
    return record;
  }
/**
 * \@internal
 * @param {?} record
 * @param {?} item
 * @return {?}
 */
_addIdentityChange(record: IterableChangeRecord_<V>, item: V) {
    record.item = item;
    if (this._identityChangesTail === null) {
      this._identityChangesTail = this._identityChangesHead = record;
    } else {
      this._identityChangesTail = this._identityChangesTail._nextIdentityChange = record;
    }
    return record;
  }
/**
 * @return {?}
 */
toString(): string {
    const /** @type {?} */ list: IterableChangeRecord_<V>[] = [];
    this.forEachItem((record: IterableChangeRecord_<V>) => list.push(record));

    const /** @type {?} */ previous: IterableChangeRecord_<V>[] = [];
    this.forEachPreviousItem((record: IterableChangeRecord_<V>) => previous.push(record));

    const /** @type {?} */ additions: IterableChangeRecord_<V>[] = [];
    this.forEachAddedItem((record: IterableChangeRecord_<V>) => additions.push(record));

    const /** @type {?} */ moves: IterableChangeRecord_<V>[] = [];
    this.forEachMovedItem((record: IterableChangeRecord_<V>) => moves.push(record));

    const /** @type {?} */ removals: IterableChangeRecord_<V>[] = [];
    this.forEachRemovedItem((record: IterableChangeRecord_<V>) => removals.push(record));

    const /** @type {?} */ identityChanges: IterableChangeRecord_<V>[] = [];
    this.forEachIdentityChange((record: IterableChangeRecord_<V>) => identityChanges.push(record));

    return 'collection: ' + list.join(', ') + '\n' +
        'previous: ' + previous.join(', ') + '\n' +
        'additions: ' + additions.join(', ') + '\n' +
        'moves: ' + moves.join(', ') + '\n' +
        'removals: ' + removals.join(', ') + '\n' +
        'identityChanges: ' + identityChanges.join(', ') + '\n';
  }
}

function DefaultIterableDiffer_tsickle_Closure_declarations() {
/** @type {?} */
DefaultIterableDiffer.prototype._length;
/** @type {?} */
DefaultIterableDiffer.prototype._collection;
/** @type {?} */
DefaultIterableDiffer.prototype._linkedRecords;
/** @type {?} */
DefaultIterableDiffer.prototype._unlinkedRecords;
/** @type {?} */
DefaultIterableDiffer.prototype._previousItHead;
/** @type {?} */
DefaultIterableDiffer.prototype._itHead;
/** @type {?} */
DefaultIterableDiffer.prototype._itTail;
/** @type {?} */
DefaultIterableDiffer.prototype._additionsHead;
/** @type {?} */
DefaultIterableDiffer.prototype._additionsTail;
/** @type {?} */
DefaultIterableDiffer.prototype._movesHead;
/** @type {?} */
DefaultIterableDiffer.prototype._movesTail;
/** @type {?} */
DefaultIterableDiffer.prototype._removalsHead;
/** @type {?} */
DefaultIterableDiffer.prototype._removalsTail;
/** @type {?} */
DefaultIterableDiffer.prototype._identityChangesHead;
/** @type {?} */
DefaultIterableDiffer.prototype._identityChangesTail;
/** @type {?} */
DefaultIterableDiffer.prototype._trackByFn;
}

/**
 * \@stable
 */
export class IterableChangeRecord_<V> implements IterableChangeRecord<V> {
  currentIndex: number|null = null;
  previousIndex: number|null = null;
/**
 * \@internal
 */
_nextPrevious: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_prev: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_next: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_prevDup: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_nextDup: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_prevRemoved: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_nextRemoved: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_nextAdded: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_nextMoved: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_nextIdentityChange: IterableChangeRecord_<V>|null = null;
/**
 * @param {?} item
 * @param {?} trackById
 */
constructor(public item: V,
public trackById: any) {}
/**
 * @return {?}
 */
toString(): string {
    return this.previousIndex === this.currentIndex ? stringify(this.item) :
                                                      stringify(this.item) + '[' +
            stringify(this.previousIndex) + '->' + stringify(this.currentIndex) + ']';
  }
}

function IterableChangeRecord__tsickle_Closure_declarations() {
/** @type {?} */
IterableChangeRecord_.prototype.currentIndex;
/** @type {?} */
IterableChangeRecord_.prototype.previousIndex;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextPrevious;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._prev;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._next;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._prevDup;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextDup;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._prevRemoved;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextRemoved;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextAdded;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextMoved;
/**
 * \@internal
 * @type {?}
 */
IterableChangeRecord_.prototype._nextIdentityChange;
/** @type {?} */
IterableChangeRecord_.prototype.item;
/** @type {?} */
IterableChangeRecord_.prototype.trackById;
}

class _DuplicateItemRecordList<V> {
/**
 * \@internal
 */
_head: IterableChangeRecord_<V>|null = null;
/**
 * \@internal
 */
_tail: IterableChangeRecord_<V>|null = null;
/**
 * Append the record to the list of duplicates.
 * 
 * Note: by design all records in the list of duplicates hold the same value in record.item.
 * @param {?} record
 * @return {?}
 */
add(record: IterableChangeRecord_<V>): void {
    if (this._head === null) {
      this._head = this._tail = record;
      record._nextDup = null;
      record._prevDup = null;
    } else { /** @type {?} */((
      // todo(vicb)
      // assert(record.item ==  _head.item ||
      //       record.item is num && record.item.isNaN && _head.item is num && _head.item.isNaN);
      this._tail))._nextDup = record;
      record._prevDup = this._tail;
      record._nextDup = null;
      this._tail = record;
    }
  }
/**
 * @param {?} trackById
 * @param {?} atOrAfterIndex
 * @return {?}
 */
get(trackById: any, atOrAfterIndex: number|null): IterableChangeRecord_<V>|null {
    let /** @type {?} */ record: IterableChangeRecord_<V>|null;
    for (record = this._head; record !== null; record = record._nextDup) {
      if ((atOrAfterIndex === null || atOrAfterIndex <= /** @type {?} */(( record.currentIndex))) &&
          looseIdentical(record.trackById, trackById)) {
        return record;
      }
    }
    return null;
  }
/**
 * Remove one {\@link IterableChangeRecord_} from the list of duplicates.
 * 
 * Returns whether the list of duplicates is empty.
 * @param {?} record
 * @return {?}
 */
remove(record: IterableChangeRecord_<V>): boolean {
    // todo(vicb)
    // assert(() {
    //  // verify that the record being removed is in the list.
    //  for (IterableChangeRecord_ cursor = _head; cursor != null; cursor = cursor._nextDup) {
    //    if (identical(cursor, record)) return true;
    //  }
    //  return false;
    //});

    const /** @type {?} */ prev: IterableChangeRecord_<V>|null = record._prevDup;
    const /** @type {?} */ next: IterableChangeRecord_<V>|null = record._nextDup;
    if (prev === null) {
      this._head = next;
    } else {
      prev._nextDup = next;
    }
    if (next === null) {
      this._tail = prev;
    } else {
      next._prevDup = prev;
    }
    return this._head === null;
  }
}

function _DuplicateItemRecordList_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
_DuplicateItemRecordList.prototype._head;
/**
 * \@internal
 * @type {?}
 */
_DuplicateItemRecordList.prototype._tail;
}

class _DuplicateMap<V> {
  map = new Map<any, _DuplicateItemRecordList<V>>();
/**
 * @param {?} record
 * @return {?}
 */
put(record: IterableChangeRecord_<V>) {
    const /** @type {?} */ key = record.trackById;

    let /** @type {?} */ duplicates = this.map.get(key);
    if (!duplicates) {
      duplicates = new _DuplicateItemRecordList<V>();
      this.map.set(key, duplicates);
    }
    duplicates.add(record);
  }
/**
 * Retrieve the `value` using key. Because the IterableChangeRecord_ value may be one which we
 * have already iterated over, we use the `atOrAfterIndex` to pretend it is not there.
 * 
 * Use case: `[a, b, c, a, a]` if we are at index `3` which is the second `a` then asking if we
 * have any more `a`s needs to return the second `a`.
 * @param {?} trackById
 * @param {?} atOrAfterIndex
 * @return {?}
 */
get(trackById: any, atOrAfterIndex: number|null): IterableChangeRecord_<V>|null {
    const /** @type {?} */ key = trackById;
    const /** @type {?} */ recordList = this.map.get(key);
    return recordList ? recordList.get(trackById, atOrAfterIndex) : null;
  }
/**
 * Removes a {\@link IterableChangeRecord_} from the list of duplicates.
 * 
 * The list of duplicates also is removed from the map if it gets empty.
 * @param {?} record
 * @return {?}
 */
remove(record: IterableChangeRecord_<V>): IterableChangeRecord_<V> {
    const /** @type {?} */ key = record.trackById;
    const /** @type {?} */ recordList: _DuplicateItemRecordList<V> = /** @type {?} */(( this.map.get(key)));
    // Remove the list of duplicates when it gets empty
    if (recordList.remove(record)) {
      this.map.delete(key);
    }
    return record;
  }
/**
 * @return {?}
 */
get isEmpty(): boolean { return this.map.size === 0; }
/**
 * @return {?}
 */
clear() { this.map.clear(); }
/**
 * @return {?}
 */
toString(): string { return '_DuplicateMap(' + stringify(this.map) + ')'; }
}

function _DuplicateMap_tsickle_Closure_declarations() {
/** @type {?} */
_DuplicateMap.prototype.map;
}

/**
 * @param {?} item
 * @param {?} addRemoveOffset
 * @param {?} moveOffsets
 * @return {?}
 */
function getPreviousIndex(
    item: any, addRemoveOffset: number, moveOffsets: number[] | null): number {
  const /** @type {?} */ previousIndex = item.previousIndex;
  if (previousIndex === null) return previousIndex;
  let /** @type {?} */ moveOffset = 0;
  if (moveOffsets && previousIndex < moveOffsets.length) {
    moveOffset = moveOffsets[previousIndex];
  }
  return previousIndex + addRemoveOffset + moveOffset;
}
